import React, { FC, memo, useState } from 'react'

interface ProgressiveImageProps {
  alt: string
  className?: string
  loading?: boolean
  loadingSrc?: string
  loadingPlaceholderSrc?: string
  placeholderSrc?: string
  src: string
  wrapperClassName?: string
}

// Helper type to enforce the relationship between `loading` and `loadingSrc`
type EnforceLoadingProps<T> = T & (
  | { loading: true | false, loadingSrc: string } // Requires loadingSrc when loading is true or false
  | { loading?: undefined, loadingSrc?: never } // Disallows loadingSrc when loading is undefined
)

const ProgressiveImage: FC<EnforceLoadingProps<ProgressiveImageProps>> = ({
  alt,
  className,
  loading,
  loadingSrc,
  loadingPlaceholderSrc,
  placeholderSrc,
  src,
  wrapperClassName
}) => {
  // Store our initial image in state for progressive loading:
  const [imgSrc, setImgSrc] = useState(typeof placeholderSrc === 'string' && placeholderSrc.length > 0 ? placeholderSrc : src)

  // Figure out our loading image based on whether or not a placeholder image exists:
  const loadingImageSource = typeof loadingPlaceholderSrc === 'string' && loadingPlaceholderSrc.length > 0
    ? loadingPlaceholderSrc
    : loadingSrc

  // Store our initial loading image in state for progressive loading:
  const [loadingImgSrc, setLoadingImgSrc] = useState(loadingImageSource)

  // Render a non-lazy-loading image when in loading state if we don't have a placeholder.
  const renderLoadingImage = loading === true && (typeof loadingImageSource === 'string' && loadingImageSource.length > 0)

  // Once the placeholder image has loaded, start download of the main image.
  const handleImageOnLoad = (): void => {
    setTimeout(() => {
      setImgSrc(src)
    }, 100)
  }

  // Once the placeholder loading image has loaded, start download of the main image.
  const handleLoadingImageOnLoad = (): void => {
    if (typeof loadingSrc === 'string' && loadingSrc.length > 0) {
      setTimeout(() => {
        setLoadingImgSrc(loadingSrc)
      }, 100)
    }
  }

  if (renderLoadingImage) {
    return <img alt={alt} src={loadingImgSrc} className={className} onLoad={handleLoadingImageOnLoad} />
  } else {
    return <img alt={alt} src={imgSrc} className={className} onLoad={handleImageOnLoad} />
  }
}

export default memo(ProgressiveImage)
